import React, { useEffect, useState, useCallback } from 'react';
import { FiTrendingUp, FiUsers } from 'react-icons/fi';
import { childrenAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ChildrenProgress = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [timeframe, setTimeframe] = useState('30d');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await childrenAPI.getProgress({ timeframe });
      const data = resp?.data?.data || resp?.data || {};
      const list = Array.isArray(data.progress) ? data.progress : (Array.isArray(data) ? data : []);
      setItems(list);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as a parent to view this page.');
      else if (status === 403) setError('Your account is not authorized to view children data.');
      else setError('Failed to load children progress');
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading progress...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Children progress</h1>
            <p className="text-sm text-slate-500 mt-0.5">Overview of associated students</p>
          </div>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)} className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-slate-200 focus:border-slate-300">
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 3 months</option>
            <option value="1y">Last year</option>
          </select>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-800">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {items.map((m, idx) => (
            <div key={idx} className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                  <span className="font-medium text-slate-900">{m.studentName || m.name || 'Student'}</span>
                </div>
                <span className="text-xs text-slate-500">{m.class || m.grade || ''}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-xs text-slate-500">Average</div>
                  <div className="text-lg font-semibold text-slate-900">{Math.round(Number(m.averageScore || m.avg || 0))}%</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-xs text-slate-500">Tests</div>
                  <div className="text-lg font-semibold text-slate-900">{m.totalTests || m.tests || 0}</div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3 text-center">
                  <div className="text-xs text-slate-500">Hours</div>
                  <div className="text-lg font-semibold text-slate-900">{Math.round((Number(m.timeSpentTotal || 0)) / 3600)}h</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <FiTrendingUp className="h-5 w-5 text-slate-500" />
            <span className="text-sm font-medium text-slate-900">Progress details</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Student</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Average</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Tests</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Recent subject</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Last active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((m, i) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{m.studentName || m.name || 'Student'}</td>
                    <td className="px-4 py-3 text-slate-600">{Math.round(Number(m.averageScore || m.avg || 0))}%</td>
                    <td className="px-4 py-3 text-slate-600">{m.totalTests || m.tests || 0}</td>
                    <td className="px-4 py-3 text-slate-600">{m.recentSubject || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{m.lastActive ? new Date(m.lastActive).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildrenProgress;
